<template>
    <div class="container content-page-wrapper projects-index pb-5" :class="{'loading': loading}">
        <div class="row pt-5">
            <div class="col-12 col-sm-8">
                <h1 class="float-left">{{ $t('Projects') }}</h1>
            </div>
            <div class="col-12 col-sm-4">
                <div class="text-right mb-3" >
                    <form class="form-inline my-2 my-lg-0" v-if="projects && this.$route.name !== 'users.show'">
                        <input class="form-control mr-sm-2 ml-sm-auto" type="search" :placeholder="$t('Project no.')" aria-label="Suche" v-model="custom_project_id">
                        <button class="btn btn-outline-primary my-2 my-sm-0" type="submit" @click.prevent="getProjects">{{ $t('Search') }}</button>
                    </form>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-6">
                <div v-if="this.$route.name === 'customerProjects.index' && $can('view_project_forecast')">
                    <div class="dropdown d-inline-block">
                        <button class="btn btn-primary btn-sm dropdown-toggle" type="button" :id="'project-export-button-forecast'" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i class="fas fa-cloud-download-alt"></i> {{ $t('Export project report') }}
                        </button>
                        <div class="dropdown-menu" :aria-labelledby="'project-edit-button-forecast'">
                            <download-pdf-button
                                :url="'/pdf/projects/forecast'"
                                :button_class="'dropdown-item'" />
                            <download-excel-button
                                :url="'/excel/projects/forecast'"
                                :button_class="'dropdown-item'" />
                        </div>
                    </div>
                </div>
                <div v-if="this.$route.name !== 'users.show' && this.$route.name !== 'customerProjects.index'">
                    <button class="btn btn-sm btn-light" @click.prevent="displayCreateProjectForm = true"><i class="fas fa-folder-plus"></i> {{ $t('Create project') }}</button>
                    <router-link :to="{ name: 'home' }" tag="button" class="btn btn-sm btn-light"><i class="fas fa-cogs"></i> {{ $t('Configure product') }}</router-link>
                </div>
            </div>
            <div class="col-6 text-right">
                <button class="btn btn-sm btn-light" @click.prevent="getProjectsByStatus" v-if="onlyActiveProjects"><i class="fas fa-filter"></i> {{ $t('Finished projects') }}</button>
                <button class="btn btn-sm btn-light" @click.prevent="getProjectsByStatus" v-if="onlyFinishedProjects"><i class="fas fa-filter"></i> {{ $t('Active projects') }}</button>
            </div>
        </div>
        <div class="row py-3" v-if="displayCreateProjectForm">
            <div class="col-12">
                <ValidationObserver v-slot="{ handleSubmit }" >
                    <form @submit.prevent="handleSubmit(submitProject)" class="create-project-form">
                        <ValidationProvider
                            :name="$t('Project name')"
                            rules="required"
                            tag="div"
                            class="form-group"
                            v-slot="{ errors }">
                            <label class="lead">{{ $t('Project name') }}</label>
                            <input v-model="projectFormFields.title" type="text" name="title" class="form-control">
                            <span class="text-danger">{{ errors[0] }}</span>
                            <div class="alert alert-danger" v-if="projectFormErrors && projectFormErrors.title">
                                {{ projectFormErrors.title[0] }}
                            </div>
                        </ValidationProvider>

                        <button type="submit" class="btn btn-primary" :disabled="projectFormSubmitting">{{ projectFormSubmitting ? $t('Creating') + '...' : $t('Create') }}</button>
                        <button class="btn text-light" :disabled="projectFormSubmitting" @click.prevent="deactivateProjectForm">{{ $t('Cancel') }}</button>
                    </form>
                </ValidationObserver>
            </div>
        </div>
        <div class="row" v-if="projects">
            <div class="col-12 m-auto">
                <div class="mt-3" v-if="projects.data">
                    <table class="table table-light mb-5">
                    <thead class="thead-dark">
                        <tr>
                            <th class="lead">{{ $t('Project') }}</th>
                            <th class="lead" v-if="$route.fullPath.includes('kundenprojekte') && $can('edit_users')">{{ $t('Customer') }}</th>
                            <th class="lead d-none d-sm-table-cell">{{ $t('Lighting lines') }}</th>
                            <th class="lead d-none d-sm-table-cell" v-if="$can('view_prices')">{{ $t('Project price') }}</th>
                            <th class="lead d-none d-sm-table-cell">{{ $t('Status') }}</th>
                            <th class="text-right lead d-none d-sm-table-cell">{{ $t('Actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="projects.meta.total === 0">
                            <td>{{ $t('No projects were found') }} (<a href="#" @click="custom_project_id = ''; getProjects();">{{ $t('load again') }}</a>)</td>
                        </tr>
                        <tr v-for="project in projects.data">
                            <td>
                                <router-link class="font-weight-bold" tag="a" :to="{ name: 'projects.show', params: { id: project.id } }" v-if="project.id">
                                    {{ project.title }}
                                </router-link> ({{ $t('No.') }} {{ project.custom_project_id }}) <project-invalid class="font-weight-bold" v-if="$project_plannings_need_update(project.plannings)" title="invalid"/><br><span class="small">{{ $t('Created') }}: {{ project.created_at }}</span></td>
                            <td v-if="$route.fullPath.includes('kundenprojekte') && $can('edit_users')"><router-link class="font-weight-bold" tag="a" :to="{ name: 'users.show', params: { id: project.user.id } }">{{ project.user.name }}</router-link><br>{{ project.user.first_name }} {{ project.user.last_name }}</td>
                            <td class="d-none d-sm-table-cell">{{ project.plannings.length }} {{ $t('configured') }}</td>
                            <td v-if="$can('view_prices')">€ {{ getProjectPrice(project.plannings) }}</td>
                            <td class="d-none d-sm-table-cell">
                                <span class="btn btn-sm btn-info no-click" @click.prevent v-if="project.project_quote && !project.project_ordered">{{ $t('Offer requested') }}: {{ project.project_quote }}</span>
                                <span class="btn btn-sm btn-warning no-click" v-else-if="project.project_ordered">{{ $t('Ordered') }}: {{ project.project_ordered }}</span>
                                <span class="btn btn-sm btn-success no-click" v-else-if="project.project_finished">{{ $t('Finished') }}: {{ project.project_finished }}</span>
                                <span class="btn btn-sm btn-light no-click" v-else>{{ $t('No offer requested') }}</span>
                            </td>
                            <td class="d-none d-sm-table-cell text-right">
                                <router-link class="btn btn-sm btn-primary text-white more-info-button" tag="a" :to="{ name: 'projects.show', params: { id: project.id } }"><i class="far fa-eye" :title="$t('Open')"></i> <span>{{ $t('Open') }}</span></router-link>
                                <div class="dropdown d-inline-block">
                                    <button class="btn btn-primary btn-sm dropdown-toggle" type="button" :id="'project-export-button-' + project.id" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fas fa-cloud-download-alt"></i> Export
                                    </button>
                                    <div class="dropdown-menu" :aria-labelledby="'project-edit-button-' + project.id">
                                        <download-pdf-button
                                            :url="'/pdf/projects/' + project.id"
                                            :button_class="'dropdown-item'" />
                                        <download-excel-button
                                            :disabled="$project_plannings_need_update(project.plannings)"
                                            :url="'/excel/projects/' + project.id"
                                            :button_class="'dropdown-item'" />
                                        <download-excel-button
                                            :button_text="$t('Export labels')"
                                            :price_option="false"
                                            :production_time_option="false"
                                            :url="'/excel/projects/' + project.id + '/labels'"
                                            :button_class="'dropdown-item'" />
                                    </div>
                                </div>
                                <button class="btn btn-sm btn-primary" @click.prevent="duplicateProject(project.id)" :disabled="$project_plannings_need_update(project.plannings) || projectDuplicating || project.user_id !== user.id"><i class="far fa-clone" :title="$t('Duplicate')"></i> <span>{{ $t('Duplicate') }}</span></button>
                                <button class="btn btn-sm btn-danger more-info-button" @click.prevent="deleteProject(project.id)" :disabled="projectDeleting || project.user_id !== user.id"><i class="far fa-trash-alt" :title="$t('Delete')"></i></button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                    <pagination :data="projects" @pagination-change-page="getProjects"></pagination>
                </div>
                <p class="text-white" v-else>{{ $t('No projects created at this time') }}</p>

            </div>
        </div>
        <div v-else>
            {{ $t('No projects') }}
        </div>
    </div>
</template>

<script>

import {mapGetters} from "vuex";
import ProjectInvalid from "../Utils/Errors/ProjectInvalid.vue";

export default {
    components: {ProjectInvalid},

    data() {
        return {
            loading: false,
            custom_project_id: '',
            projects: {},
            projectFormFields: {
                title: '',
            },
            projectFormErrors: {},
            projectFormSubmitting: false,
            projectDeleting: false,
            dialuxData: [],
            projectDuplicating: false,
            displayCreateProjectForm: false,
            onlyActiveProjects: true,
            onlyFinishedProjects: false,
        };
    },

    mounted() {
        this.getProjects();
    },

    watch: {
        $route(to, from) {
            this.getProjects();
        },
    },

    computed: {
        ...mapGetters({
            authenticated: 'auth/authenticated',
            user: 'auth/user',
            permissions: 'auth/permissions',
            latestUpdateNotification: 'updateNotification/latestUpdateNotification',
        }),


    },

    methods: {

		getProjects(page = 1) {
		    this.loading = true;
			axios.get('/api/projects?page=' + page + '&paginate=25' + (this.$route.params.id && this.$route.fullPath.includes('benutzer/') ? '&user_id=' + this.$route.params.id : '')  + (this.custom_project_id ? '&custom_project_id=' + this.custom_project_id : '') + (this.$route.fullPath.includes('kundenprojekte') ? '&customer_projects=1' : '') + '&only_active=' + this.onlyActiveProjects + '&only_finished=' + this.onlyFinishedProjects)
				.then(response => {
					this.projects = response.data;
					this.loading = false;
				}).catch(error => {
                console.log(error);
                this.loading = false;
            });
        },

        getProjectsByStatus() {

            if (this.onlyFinishedProjects && !this.onlyActiveProjects) {
                this.onlyFinishedProjects = false;
                this.onlyActiveProjects = true;
            } else {
                this.onlyFinishedProjects = true;
                this.onlyActiveProjects = false;
            }

            this.getProjects();
        },

        getProjectPrice(plannings) {
            let sum = 0;
            if ( plannings ) {
                plannings.map( function(element) {
                    if ( element.reduced_total_price_of_lumination_lines && element.reduced_total_price_of_lumination_lines > 0 && element.reduced_total_price_of_lumination_lines !== element.total_price_of_lumination_lines) {
                        sum += element.reduced_total_price_of_lumination_lines
                    } else if ( element.total_price_of_lumination_lines) {
                        sum += element.total_price_of_lumination_lines
                    }
                });
            }
            return sum.toFixed(2);;
        },

        submitProject() {
            this.projectFormSubmitting = true;
            this.loading = true;
            axios.post('/api/projects', this.projectFormFields )
                .then(response => {
                    this.projectFormSubmitting = false;
                    this.deactivateProjectForm();
                    this.getProjects();
                    this.$swal.fire({
                        icon: 'success',
                        title: this.$t('Project created'),
                        confirmButtonColor: '#3CA2A4'
                    });
                }).catch(error => {
                    this.loading = false;
                if (error.response.status === 422) { // Validation Error
                    this.projectFormErrors = error.response.data.errors;
                    this.$swal.fire({
                        icon: 'error',
                        title: this.$t('There was an error'),
                        text: this.$t('Please check the form'),
                        confirmButtonColor: '#3CA2A4'
                    });
                }
                this.projectFormSubmitting = false;
            });
        },

        deactivateProjectForm() {
            this.displayCreateProjectForm = false;
            this.projectFormFields.title = '';
        },

        duplicateProject(project_id) {

            this.$swal.fire({
                title: this.$t('Project name'),
                text: this.$t('Please enter the name of the project'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Duplicate'),
                cancelButtonText: this.$t('Cancel'),
                input: 'text',
                inputValidator: (value) => {
                    if ( !value ) {
                        return this.$t('Please enter a valid name')
                    }
                    if ( typeof value !== 'string' ) {
                        return this.$t('Please enter a valid name without any special characters')
                    }
                }
            }).then((result) => {

                if (result.value) {
                    this.projectDuplicating = true;
                    this.loading = true;
                    let projectTitle = { title: result.value };

                    axios.put('/api/projects/' + project_id + '/duplicate', projectTitle)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Duplicated successfully'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.projectDuplicating = false;
                            this.getProjects();
                            this.loading = false;
                        }).catch(error => {
                            this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                            this.projectDuplicating = false;
                            this.loading = false;
                    });
                }
            })
        },

        deleteProject(project_id) {
            this.$swal.fire({
                title: this.$t('Are you sure?'),
                text: this.$t("After deleting the project, the data can not be restored."),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e3342f',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t("Yes, delete!"),
                cancelButtonText: this.$t("Cancel")
            }).then((result) => {
                if (result.value) {
                    this.projectDeleting = true;
                    this.loading = true;

                    axios.delete('/api/projects/' + project_id)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Deleted successfully')
                            });
                            this.getProjects();
                            this.projectDeleting = false;
                            this.loading = false;
                        }).catch(error => {
                            this.$swal.fire({ icon: 'error', title: this.$t('There was an error')});
                            this.loading = false;
                    });

                }
            })
        },

	},
};
</script>
